import React, { useState, useEffect, useRef, useCallback } from 'react'
import {
  Box,
  VStack,
  Field,
  Input,
  Textarea,
  Button,
  HStack,
  Text
} from '@chakra-ui/react'

export interface ProfileFormData {
  name: string
  bio: string
  specialty: string
  email?: string
  phone?: string
  website?: string
  location?: string
  [key: string]: any // Allow additional fields
}

interface ProfileInterviewFormProps {
  onSubmit: (data: ProfileFormData) => void
  onSave?: (data: ProfileFormData) => void
}

export const ProfileInterviewForm: React.FC<ProfileInterviewFormProps> = ({
  onSubmit,
  onSave
}) => {
  const [formData, setFormData] = useState<ProfileFormData>(() => {
    // Initialize from localStorage if available
    const saved = localStorage.getItem('profileFormData')
    if (saved) {
      try {
        return JSON.parse(saved)
      } catch (e) {
        console.error('Failed to parse saved form data:', e)
      }
    }
    return {
      name: '',
      bio: '',
      specialty: ''
    }
  })
  const onSaveRef = useRef(onSave)

  // Keep onSave ref up to date without triggering effects
  useEffect(() => {
    onSaveRef.current = onSave
  }, [onSave])

  // Auto-save to localStorage when formData changes
  useEffect(() => {
    // Only save if there's meaningful data
    if (formData.name || formData.bio || formData.specialty) {
      const dataToSave = JSON.stringify(formData)
      const currentSaved = localStorage.getItem('profileFormData')
      
      // Only save if data actually changed (prevents unnecessary saves on mount)
      if (dataToSave !== currentSaved) {
        localStorage.setItem('profileFormData', dataToSave)
        // Use ref to avoid dependency on onSave
        onSaveRef.current?.(formData)
      }
    }
  }, [formData])

  const handleChange = (field: keyof ProfileFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (formData.name && formData.bio && formData.specialty) {
      onSubmit(formData)
    }
  }

  return (
    <Box
      as="form"
      onSubmit={handleSubmit}
      p={{ base: 4, md: 6 }}
      bg="white"
      borderWidth="1px"
      borderColor="gray.200"
      borderRadius="xl"
      boxShadow="sm"
    >
      <VStack gap={{ base: 4, md: 6 }} align="stretch">
        <Field.Root required>
          <Field.Label>Full Name</Field.Label>
          <Input
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            placeholder="Enter your full name"
            size="lg"
          />
        </Field.Root>

        <Field.Root required>
          <Field.Label>Specialty</Field.Label>
          <Box
            as="select"
            value={formData.specialty}
            onChange={(e) => handleChange('specialty', e.target.value)}
            w="full"
            h="48px"
            px={4}
            fontSize="md"
            borderRadius="lg"
            borderWidth="1px"
            borderColor="gray.300"
            bg="white"
            _hover={{ borderColor: 'gray.400' }}
            _focus={{ borderColor: 'brand.500', boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)', outline: 'none' }}
          >
            <option value="">Select your specialty</option>
            <option value="Nutritionist">Nutritionist</option>
            <option value="Health Coach">Health Coach</option>
            <option value="Wellness Practitioner">Wellness Practitioner</option>
            <option value="Fitness Trainer">Fitness Trainer</option>
            <option value="Mental Health Counselor">Mental Health Counselor</option>
            <option value="Life Coach">Life Coach</option>
            <option value="Other">Other</option>
          </Box>
        </Field.Root>

        <Field.Root required>
          <Field.Label>Bio / About You</Field.Label>
          <Textarea
            value={formData.bio}
            onChange={(e) => handleChange('bio', e.target.value)}
            placeholder="Tell us about yourself, your practice, and what makes you unique..."
            rows={6}
            size="lg"
          />
          <Text fontSize="sm" color="gray.500" mt={2}>
            This will be used to create your Spotlight page and ad creatives.
          </Text>
        </Field.Root>

        <Field.Root>
          <Field.Label>Email (optional)</Field.Label>
          <Input
            type="email"
            value={formData.email || ''}
            onChange={(e) => handleChange('email', e.target.value)}
            placeholder="your@email.com"
            size="lg"
          />
        </Field.Root>

        <Field.Root>
          <Field.Label>Website (optional)</Field.Label>
          <Input
            type="url"
            value={formData.website || ''}
            onChange={(e) => handleChange('website', e.target.value)}
            placeholder="https://yourwebsite.com"
            size="lg"
          />
        </Field.Root>

        <Field.Root>
          <Field.Label>Location (optional)</Field.Label>
          <Input
            value={formData.location || ''}
            onChange={(e) => handleChange('location', e.target.value)}
            placeholder="City, State/Country"
            size="lg"
          />
        </Field.Root>

        <HStack justify="flex-end" pt={{ base: 2, md: 4 }}>
          <Button
            type="submit"
            colorPalette="brand"
            size="lg"
            disabled={!formData.name || !formData.bio || !formData.specialty}
            w={{ base: 'full', md: 'auto' }}
          >
            Continue to Account Setup
          </Button>
        </HStack>
      </VStack>
    </Box>
  )
}


